import { getRouteApi, useNavigate, useRouter } from "@tanstack/react-router";

import type { FileRoutesById } from "@/routeTree.gen";

import { useFilterUrlKeys } from "../filter-url-keys";
import type { SortDirection } from "../sort-direction";
import { formatSortParam, parseSortParam } from "../sort-param";

export type { SortDirection };

// TanStack search is dynamically shaped per route, hence the loose record.
type AnySearch = Record<string, any>;

/**
 * Read/write the single canonical query-params SORT STRING in the current
 * route's URL state — `field` (ascending) / `-field` (descending), the same
 * wire shape the HTTP API uses (see `docs/query-params.md`: "no URL-state
 * carve-out"). This replaces the legacy object-shaped `sorts` writer
 * (`filters/_hooks` `useGetSorts`/`useModifySorts`) for admin list tables, so
 * the address bar reads `?sort=-total` instead of the deprecated
 * `?sorts[total]=desc` object form. Mirrors `useFilters`' route-state plumbing.
 *
 * Single-sort model (matches the table headers): one active field at a time.
 * The key is the nearest `FilterUrlKeysProvider`'s (`sort` without one).
 */
export function useSortParam<
  TId extends keyof FileRoutesById = keyof FileRoutesById,
>() {
  const router = useRouter();
  const routeId = router.state.matches.at(-1)?.routeId as TId;
  const navigate = useNavigate();
  const routeApi = getRouteApi(routeId);
  const search = routeApi.useSearch() as AnySearch;
  const keys = useFilterUrlKeys();

  const sort = search[keys.sort] as string | undefined;

  const directionFor = (field: string): SortDirection | undefined => {
    const parsed = parseSortParam(sort);
    return parsed && parsed.field === field ? parsed.direction : undefined;
  };

  /** Set (or clear, when `next` is undefined) the sort string directly. */
  const setSort = (next: string | undefined) => {
    const newSearch: AnySearch = {
      ...search,
      [keys.sort]: next || undefined,
    };
    navigate({
      search: newSearch as Parameters<typeof navigate>[0]["search"],
      replace: true,
    });
  };

  /** Cycle a column: none → asc (`field`) → desc (`-field`) → none. */
  const toggle = (field: string) => {
    const current = directionFor(field);
    const next =
      current === "asc"
        ? formatSortParam(field, "desc")
        : current === "desc"
          ? undefined
          : formatSortParam(field, "asc");
    setSort(next);
  };

  return { sort, directionFor, toggle, setSort };
}
